import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageElement } from "../VisualPageBuilder";
import { sanitizeVisualBuilderHTML, escapeHTML } from "@/utils/htmlSanitization";
import { 
  Star, 
  CheckCircle, 
  Play, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  Users,
  BarChart3
} from "lucide-react";

interface ElementRendererProps {
  element: PageElement;
  globalStyles: any;
  activeView: 'desktop' | 'tablet' | 'mobile';
  isSelected: boolean;
  onUpdate: (updates: Partial<PageElement>) => void;
}

export default function ElementRenderer({ 
  element, 
  globalStyles, 
  activeView, 
  isSelected,
  onUpdate 
}: ElementRendererProps) {
  const [isEditing, setIsEditing] = useState(false);
  
  // Apply responsive styles
  const getResponsiveStyles = () => {
    const baseStyles = { ...element.styles };
    const responsiveStyles = element.settings.responsive?.[activeView] || {};
    return { ...baseStyles, ...responsiveStyles };
  };

  const appliedStyles = getResponsiveStyles();

  // Handle inline editing
  const handleDoubleClick = useCallback(() => {
    if (['text', 'heading', 'button'].includes(element.type)) {
      setIsEditing(true);
    }
  }, [element.type]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleContentChange = useCallback((field: string, value: any) => {
    onUpdate({
      content: {
        ...element.content,
        [field]: value
      }
    });
  }, [element.content, onUpdate]);

  // Convert styles to CSS
  const stylesToCSS = (styles: any) => ({
    padding: styles.padding,
    margin: styles.margin,
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    borderRadius: styles.borderRadius,
    border: styles.border,
    fontSize: styles.fontSize,
    fontWeight: styles.fontWeight,
    textAlign: styles.textAlign,
    width: styles.width,
    height: styles.height,
    ...styles
  });

  const elementStyle = stylesToCSS(appliedStyles);

  // Render based on element type
  const renderElement = () => {
    switch (element.type) {
      case 'text':
        return (
          <div 
            style={elementStyle}
            onDoubleClick={handleDoubleClick}
            className="min-h-[1em] cursor-text"
          >
            {isEditing ? (
              <Textarea
                value={element.content.text || ''}
                onChange={(e) => handleContentChange('text', e.target.value)}
                onBlur={handleBlur}
                autoFocus
                className="border-none p-0 resize-none"
                style={{ ...elementStyle, border: 'none', outline: 'none' }}
              />
            ) : (
              <div dangerouslySetInnerHTML={{ 
                __html: sanitizeVisualBuilderHTML(element.content.text || 'Text hier eingeben...') 
              }} />
            )}
          </div>
        );

      case 'heading':
        const HeadingTag = element.content.tag || 'h2';
        return (
          <HeadingTag 
            style={elementStyle}
            onDoubleClick={handleDoubleClick}
            className="min-h-[1em] cursor-text"
          >
            {isEditing ? (
              <Input
                value={element.content.text || ''}
                onChange={(e) => handleContentChange('text', e.target.value)}
                onBlur={handleBlur}
                autoFocus
                className="border-none p-0"
                style={{ ...elementStyle, border: 'none', outline: 'none' }}
              />
            ) : (
              element.content.text || 'Überschrift hier eingeben...'
            )}
          </HeadingTag>
        );

      case 'button':
        return (
          <Button
            style={elementStyle}
            onDoubleClick={handleDoubleClick}
            className="cursor-pointer"
            variant={element.content.variant || 'default'}
          >
            {isEditing ? (
              <Input
                value={element.content.text || ''}
                onChange={(e) => handleContentChange('text', e.target.value)}
                onBlur={handleBlur}
                autoFocus
                className="border-none p-0 bg-transparent text-current"
              />
            ) : (
              element.content.text || 'Button Text'
            )}
          </Button>
        );

      case 'image':
        return (
          <div style={elementStyle} className="overflow-hidden">
            <img
              src={element.content.src || 'https://via.placeholder.com/400x300'}
              alt={element.content.alt || 'Bild'}
              className="w-full h-auto object-cover"
              style={{ borderRadius: elementStyle.borderRadius }}
            />
            {element.content.caption && (
              <p className="text-sm text-muted-foreground mt-2 text-center">
                {element.content.caption}
              </p>
            )}
          </div>
        );

      case 'hero':
        return (
          <div 
            style={elementStyle} 
            className="relative flex items-center justify-center text-center"
          >
            {element.content.backgroundImage && (
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${element.content.backgroundImage})` }}
              />
            )}
            <div className="relative z-10 max-w-4xl mx-auto space-y-6">
              <h1 className="text-4xl md:text-6xl font-bold">
                {element.content.title || 'Willkommen'}
              </h1>
              {element.content.subtitle && (
                <p className="text-xl md:text-2xl opacity-90">
                  {element.content.subtitle}
                </p>
              )}
              {element.content.ctaText && (
                <Button size="lg" className="text-lg px-8 py-3">
                  {element.content.ctaText}
                </Button>
              )}
            </div>
          </div>
        );

      case 'benefits':
        return (
          <div style={elementStyle} className="space-y-8">
            {element.content.title && (
              <h2 className="text-3xl font-bold text-center">
                {element.content.title}
              </h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(element.content.items || []).map((item: any, index: number) => (
                <div key={index} className="text-center space-y-3">
                  <div className="w-12 h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'testimonial':
        return (
          <Card style={elementStyle}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {element.content.avatar && (
                  <img
                    src={element.content.avatar}
                    alt={element.content.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div className="flex-1">
                  <div className="flex mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <blockquote className="text-lg italic mb-3">
                    "{element.content.quote}"
                  </blockquote>
                  <div>
                    <div className="font-semibold">{element.content.author}</div>
                    {element.content.position && (
                      <div className="text-sm text-muted-foreground">
                        {element.content.position}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'testimonials':
        return (
          <div style={elementStyle} className="space-y-8">
            {element.content.title && (
              <h2 className="text-3xl font-bold text-center">
                {element.content.title}
              </h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(element.content.testimonials || []).map((testimonial: any, index: number) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      <blockquote className="italic">
                        "{testimonial.quote}"
                      </blockquote>
                      <div className="flex items-center gap-3">
                        {testimonial.avatar && (
                          <img
                            src={testimonial.avatar}
                            alt={testimonial.author}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <div className="font-semibold text-sm">{testimonial.author}</div>
                          <div className="text-xs text-muted-foreground">{testimonial.position}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'contact':
        return (
          <Card style={elementStyle}>
            <CardHeader>
              <CardTitle>
                {element.content.title || 'Kontaktieren Sie uns'}
              </CardTitle>
              {element.content.description && (
                <CardDescription>{element.content.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {(element.content.fields || []).map((field: any, index: number) => (
                <div key={index}>
                  <label className="text-sm font-medium">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <Textarea placeholder={field.placeholder} className="mt-1" />
                  ) : (
                    <Input 
                      type={field.type} 
                      placeholder={field.placeholder} 
                      className="mt-1"
                    />
                  )}
                </div>
              ))}
              <Button className="w-full">
                {element.content.submitText || 'Nachricht senden'}
              </Button>
            </CardContent>
          </Card>
        );

      case 'spacer':
        return (
          <div 
            style={{
              height: element.content.height || '32px',
              width: '100%',
              ...elementStyle
            }}
            className="bg-transparent"
          />
        );

      case 'divider':
        return (
          <div style={elementStyle} className="flex items-center justify-center py-4">
            <div 
              className="border-t border-border"
              style={{ 
                width: element.content.width || '100%',
                borderColor: element.styles.color || 'currentColor'
              }}
            />
          </div>
        );

      case 'video':
        return (
          <div style={elementStyle} className="relative">
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
              <Play className="w-12 h-12 text-muted-foreground" />
            </div>
            {element.content.title && (
              <h3 className="mt-3 text-lg font-semibold">{element.content.title}</h3>
            )}
          </div>
        );

      case 'gallery':
        return (
          <div style={elementStyle}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {(element.content.images || Array(8).fill(null)).map((image: any, index: number) => (
                <div key={index} className="aspect-square bg-muted rounded-lg overflow-hidden">
                  {image ? (
                    <img 
                      src={image.src} 
                      alt={image.alt} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-muted to-muted-foreground/10" />
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'dropdown':
        return (
          <div style={elementStyle} className="space-y-2">
            {element.content.label && (
              <label className="text-sm font-medium">
                {element.content.label}
              </label>
            )}
            <Select defaultValue="">
              <SelectTrigger className="w-full bg-background">
                <SelectValue placeholder={element.content.placeholder || 'Bitte wählen Sie...'} />
              </SelectTrigger>
              <SelectContent className="bg-popover border shadow-md z-50">
                {(element.content.options || []).map((option: any, index: number) => (
                  <SelectItem key={index} value={option.value || `option-${index}`}>
                    {option.label || `Option ${index + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      default:
        return (
          <div 
            style={elementStyle}
            className="p-4 border-2 border-dashed border-muted-foreground/50 rounded-lg text-center"
          >
            <p className="text-muted-foreground">
              Element: {element.type}
            </p>
            <p className="text-sm text-muted-foreground/70">
              Noch nicht implementiert
            </p>
          </div>
        );
    }
  };

  return (
    <div className={`relative ${isSelected ? 'ring-2 ring-primary ring-offset-1' : ''}`}>
      {renderElement()}
    </div>
  );
}